import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, Button, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, AlertDialog } from '@/components/shared/ui';
import { ToggleLabel } from '@/components/shared/ui/Toggle';
import CurriculumSetup from '../product/CurriculumSetup';
import AddScenarioModal from '../product/AddScenarioModal';
import { Plus, Upload, Loader2, AlertCircle } from 'lucide-react';
import { CurriculumItem, VideoLecture } from '../../types/curriculum';
import { ScenarioDetail } from '../../data/scenarioDetails';
import type { ScenarioAdminDto } from '@/types/api/scenario';
import { type ClassProductTypeFilter } from './classFormUtils';
import { useOrganizations, useOrganizationTitleList } from '../../hooks/useOrganization';
import { ROUTES } from '../../lib/constants/routes';
import { useClassDetail, useOrderUserCount, useCreateClass, useUpdateClass } from '../../data/queries/useClasses';
import { useProductList, useClassFormProductDetail, useOrganizationPurchasedProducts } from '../../hooks/useProduct';
import { isContentLikeProductType, isInstitutionClassProductSelectType } from '@/utils/productUtils';
import { buildClassPriceSelectOptions } from '@/lib/classPriceSelectOptions';
import { findPurchasedRowForProduct, resolvePurchasedProductPriceId } from '@/lib/resolvePurchasedProductPriceId';
import { formatSalesUnitMoneyCell } from '@/lib/productSalesUnitTableDisplay';
import { mapPurchasedProductListToSelectItems } from '@/services/productService';
import type { ProductListItemDto } from '@/types/api/product';
import type { ClassItemInfoDto, GetClassDetailResponseDto } from '@/types/api/class';
import { pickClassItemExpectedPlaySeconds } from '@/utils/classUtils';
import { uploadFileViaPresignedUrl } from '@/services/fileService';
import { ApiError } from '@/services/apiClient';
import { useTranslationWithLoading } from '@/hooks/useTranslationWithLoading';
import {
    buildClassTranslationTriple,
    coalesceEmptyTitleDescSlots,
    createEmptyTitleDescTriple,
    mergeTitleDescriptionTripleFromClassLike,
    translateTitleDescTripleIfNeeded,
    type TitleDescTriple,
} from '@/lib/apiTranslations';
import { resolveTripleLocaleForEntityForm } from '@/lib/localeTripleUi';
import type { ClassTranslationInput } from '@/types/api/class';

function durationLabelFromPlaySeconds(sec: number | undefined): string {
	if (sec == null || sec <= 0) return '0분';
	return `${Math.max(1, Math.ceil(sec / 60))}분`;
}

function resolveOrderAvailableCapacity(
    data: { totalQuantity: number | null; usedCapacity: number | null; availableCapacity: number | null } | undefined
): number | null | undefined {
    if (data == null) return undefined;
    if (typeof data.availableCapacity === 'number' && Number.isFinite(data.availableCapacity)) {
        return data.availableCapacity;
    }
    if (typeof data.totalQuantity === 'number' && Number.isFinite(data.totalQuantity)) {
        const used = typeof data.usedCapacity === 'number' && Number.isFinite(data.usedCapacity)
            ? data.usedCapacity
            : 0;
        return Math.max(0, data.totalQuantity - used);
    }
    return null;
}

interface CompletionRequirement {
    minScenarios: number;
    minPassingScore: number;
    minScenarioMode: 'all' | 'custom';
}

/**
 * 수강기간 종료일과 프로덕트 유효기간으로 모집기간 최대 종료일 계산
 * @param courseEndDate 수강기간 종료일 (YYYY-MM-DD)
 * @param validityPeriod 유효기간 (숫자)
 * @param validityPeriodUnit 유효기간 단위 (DAY, MONTH, YEAR)
 * @returns 모집기간 최대 종료일 (YYYY-MM-DD) 또는 null
 */
function calculateMaxRecruitmentEndDate(
    courseEndDate: string | undefined,
    validityPeriod: number | null | undefined,
    validityPeriodUnit: string | null | undefined
): string | null {
    if (!courseEndDate || validityPeriod == null || validityPeriod <= 0) return null;
    
    const endDate = new Date(courseEndDate);
    if (isNaN(endDate.getTime())) return null;
    
    // validityPeriod만큼 빼기
    if (validityPeriodUnit === 'DAY') {
        endDate.setDate(endDate.getDate() - validityPeriod);
    } else if (validityPeriodUnit === 'MONTH') {
        endDate.setMonth(endDate.getMonth() - validityPeriod);
    } else if (validityPeriodUnit === 'YEAR') {
        endDate.setFullYear(endDate.getFullYear() - validityPeriod);
    } else {
        return null;
    }
    
    return endDate.toISOString().split('T')[0];
}

export interface ClassEditProps {
    /** 기관 관리 페이지 등 embed 모드에서 사용 시 classId를 prop으로 전달 */
    classIdProp?: number;
    /** 취소/뒤로가기 시 이동할 경로 (기본값: 클래스 관리 페이지) */
    returnPath?: string;
}

export default function ClassEdit(props?: ClassEditProps): React.ReactElement {
    const { classIdProp, returnPath } = props ?? {};
    const { t, i18n } = useTranslation('class');
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const { getLicenseInfo, getCurrentRole, user } = useAuth();
    const licenseInfo = getLicenseInfo();
    const isAdmin = getCurrentRole() === 'admin';
    const isMaster = getCurrentRole() === 'master';
    const classId = useMemo(() => {
        // prop으로 전달된 classId가 있으면 우선 사용 (embed 모드)
        if (classIdProp != null && classIdProp > 0) return classIdProp;
        // 아니면 URL params에서 가져오기 (일반 모드)
        const n = Number(id);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }, [classIdProp, id]);

    const { data: classDetail, isLoading: detailLoading, error: detailError } = useClassDetail(classId);
    const { withTranslationLoading } = useTranslationWithLoading();

    // Determine base path based on current route
    const isMasterRoute = location.pathname.includes('/master/');
    const classManagementPath = returnPath ?? (isMasterRoute ? ROUTES.MASTER.CLASS_MANAGEMENT : ROUTES.ADMIN.CLASS_MANAGEMENT);
    const isOpenClassRoute = location.pathname.includes('open-class');
    const isDuplicateMode = location.pathname.includes('/duplicate/');

    // Form state
    const today = new Date().toISOString().split('T')[0];
    const [classI18n, setClassI18n] = useState<TitleDescTriple>(() => createEmptyTitleDescTriple());
    const formLocale = useMemo(() => resolveTripleLocaleForEntityForm(i18n.language), [i18n.language]);
    const [thumbnail, setThumbnail] = useState<string>('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [recruitmentStartDate, setRecruitmentStartDate] = useState(today);
    const [recruitmentEndDate, setRecruitmentEndDate] = useState(today);
    const [maxParticipants, setMaxParticipants] = useState<number>(30);
    const [curriculumItems, setCurriculumItems] = useState<CurriculumItem[]>([]);
    const [completionRequirements, setCompletionRequirements] = useState<CompletionRequirement>({
        minScenarios: 1,
        minPassingScore: 70,
        minScenarioMode: 'all',
    });
    const [useCompletionRequirements, setUseCompletionRequirements] = useState(false);

    // Admin-specific state
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');

    // 클래스 유형(오픈클래스/기관클래스) → URL 경로로 초기값 결정
    const [classType, setClassType] = useState<ClassProductTypeFilter>(isOpenClassRoute ? 'openClass' : 'orgClass');
    const isOpenClassEdit = classType === 'openClass';
    const [selectedProductPriceId, setSelectedProductPriceId] = useState<number>(0);
    const [selectedSalesUnitId, setSelectedSalesUnitId] = useState<number>(0);
    const [participationMethod, setParticipationMethod] = useState<'online' | 'inPerson'>('online');
    const [isActive, setIsActive] = useState<boolean>(true);
    const [partnerOrgId, setPartnerOrgId] = useState<string>('');

    const orgNumericIdForPurchased =
        !isOpenClassEdit && selectedOrganizationId.trim() !== ''
            ? Number(selectedOrganizationId)
            : 0;
    const usePurchasedProductList =
        !isOpenClassEdit &&
        Number.isFinite(orgNumericIdForPurchased) &&
        orgNumericIdForPurchased > 0;

    /** 오픈클래스 수정: 등록 가능 인원 조회에 GET /product/organization/purchased 로 주문 행의 productPriceId(예: 30인용)를 맞추기 위해 구매 기관 id 필요 */
    const purchasedProductsOrgIdForQuery = useMemo(() => {
        if (isOpenClassEdit) {
            if (!classDetail) return 0;
            if (isMaster) {
                const m = Number(user?.currentAccount?.organizationId);
                if (Number.isFinite(m) && m > 0) return Math.floor(m);
            }
            const cid = Number(classDetail.organizationId);
            return Number.isFinite(cid) && cid > 0 ? Math.floor(cid) : 0;
        }
        return orgNumericIdForPurchased;
    }, [isOpenClassEdit, classDetail, isMaster, user?.currentAccount?.organizationId, orgNumericIdForPurchased]);

    const fetchPurchasedProductsEnabled =
        Number.isFinite(purchasedProductsOrgIdForQuery) &&
        purchasedProductsOrgIdForQuery > 0 &&
        (usePurchasedProductList || (isOpenClassEdit && !!classDetail));

    // 오픈클래스만 GET /product/list — 기관클래스는 GET /product/organization/purchased 만
    const { data: productListData } = useProductList(
        { page: 1, pageSize: 500 },
        { enabled: isOpenClassEdit }
    );

    const { data: purchasedProductsData, isFetching: purchasedProductsFetching } =
        useOrganizationPurchasedProducts(purchasedProductsOrgIdForQuery, {
            enabled: fetchPurchasedProductsEnabled,
        });

    const purchasedList = purchasedProductsData?.productList ?? [];
    const purchasedSelectItems = useMemo(
        () =>
            mapPurchasedProductListToSelectItems(purchasedList, undefined, {
                institutionClassProductSelect: !isOpenClassEdit,
            }),
        [purchasedList, isOpenClassEdit]
    );

    // productPriceId를 포함한 확장 타입
    type ProductOptionWithPriceId = ProductListItemDto & { productPriceId?: number };

    const productOptions = useMemo((): ProductOptionWithPriceId[] => {
        const result: ProductOptionWithPriceId[] = [];
        const seenPriceIds = new Set<number>();

        // 오픈클래스든 기관클래스든 purchasedList에서 productPriceId 포함해서 매핑
        for (const row of purchasedList) {
            if (!row.productPriceId || seenPriceIds.has(row.productPriceId)) continue;
            
            // 오픈클래스: CONTENT 타입만, 기관클래스: 기관용 타입만
            const typeCheck = isOpenClassEdit 
                ? isContentLikeProductType(row.type ?? 'CONTENT')
                : isInstitutionClassProductSelectType(row.type ?? 'CONTENT');
            if (!typeCheck) continue;

            seenPriceIds.add(row.productPriceId);
            const productTitle = row.productTitle?.trim() || '';
            const priceTitle = row.productPriceTitle?.trim() || '';
            const displayTitle = priceTitle ? `${productTitle} (${priceTitle})` : productTitle;

            result.push({
                productId: row.productId,
                productPriceId: row.productPriceId,
                title: displayTitle,
                type: row.type ?? 'CONTENT',
                scenarioCount: 0,
                lectureCount: 0,
                priceCount: 0,
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                premiumTypes: [],
            });
        }

        // 현재 선택된 productPriceId가 목록에 없으면 추가 (수정 시 구매 목록에 없을 수도 있음)
        if (selectedProductPriceId > 0 && !seenPriceIds.has(selectedProductPriceId)) {
            const title =
                classDetail?.productTitle?.trim() ||
                classDetail?.title?.trim() ||
                `프로덕트 #${selectedProductPriceId}`;
            result.push({
                productId: classDetail?.productId ?? 0,
                productPriceId: selectedProductPriceId,
                title,
                type: 'CONTENT',
                scenarioCount: 0,
                lectureCount: 0,
                priceCount: 0,
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                premiumTypes: [],
            });
        }

        return result;
    }, [
        purchasedList,
        isOpenClassEdit,
        selectedProductPriceId,
        classDetail?.productTitle,
        classDetail?.title,
        classDetail?.productId,
    ]);
    /** 기관클래스 전용: 구매 목록(GET /product/organization/purchased, isLicenseActive=true 주문만) 조회가 끝났는데 현재 프로덕트가 없으면 비활성 주문의 프로덕트. 오픈클래스는 주문이 없으므로 제외 */
    const isInactiveOrderProduct = useMemo(() => {
        if (isOpenClassEdit) return false;
        if (!fetchPurchasedProductsEnabled || purchasedProductsFetching || !purchasedProductsData) return false;
        if (selectedProductPriceId <= 0) return false;
        return !purchasedList.some((row) => row.productPriceId === selectedProductPriceId);
    }, [isOpenClassEdit, fetchPurchasedProductsEnabled, purchasedProductsFetching, purchasedProductsData, selectedProductPriceId, purchasedList]);

    const selectedProductMeta = useMemo(() => {
        // 오픈클래스, 기관클래스 모두 productPriceId로 찾기
        const p = productOptions.find((x) => x.productPriceId === selectedProductPriceId);
        return { 
            productId: p?.productId ?? 0,
            productPriceId: p?.productPriceId ?? 0,
            title: p?.title ?? '', 
            type: p?.type 
        };
    }, [productOptions, selectedProductPriceId]);

    const orgClassProductPriceId = useMemo(() => {
        if (isOpenClassEdit) return 0;
        // 기관클래스: selectedProductPriceId 그대로 사용
        return selectedProductPriceId;
    }, [isOpenClassEdit, selectedProductPriceId]);

    const orgPurchasedRowForPayload = useMemo(() => {
        if (isOpenClassEdit || selectedProductMeta.productId <= 0) return undefined;
        // 선택된 productPriceId와 정확히 매칭되는 행 찾기
        if (selectedProductPriceId > 0) {
            const exactMatch = purchasedList.find(
                (row) => row.productId === selectedProductMeta.productId && row.productPriceId === selectedProductPriceId
            );
            if (exactMatch) return exactMatch;
        }
        // productPriceId가 선택되지 않았으면 기존 로직 사용
        return findPurchasedRowForProduct(purchasedList, selectedProductMeta.productId, classDetail?.orderId ?? undefined);
    }, [isOpenClassEdit, selectedProductMeta.productId, selectedProductPriceId, purchasedList, classDetail?.orderId]);

    const openClassPurchasedRow = useMemo(() => {
        if (!isOpenClassEdit || selectedProductMeta.productId <= 0 || !classDetail) return undefined;
        // 선택된 salesUnitId(productPriceId)와 정확히 매칭되는 행 찾기
        if (selectedSalesUnitId > 0) {
            const exactMatch = purchasedList.find(
                (row) => row.productId === selectedProductMeta.productId && row.productPriceId === selectedSalesUnitId
            );
            if (exactMatch) return exactMatch;
        }
        // salesUnitId가 선택되지 않았으면 기존 로직 사용
        return findPurchasedRowForProduct(purchasedList, selectedProductMeta.productId, classDetail.orderId ?? undefined);
    }, [isOpenClassEdit, selectedProductMeta.productId, selectedSalesUnitId, purchasedList, classDetail]);

    const openClassResolvedProductPriceId = useMemo(() => {
        if (!isOpenClassEdit || selectedProductMeta.productId <= 0) return 0;
        // 사용자가 선택한 판매단위가 있으면 그것을 우선 사용
        if (selectedSalesUnitId > 0) {
            const exactMatch = purchasedList.find(
                (row) => row.productId === selectedProductMeta.productId && row.productPriceId === selectedSalesUnitId
            );
            if (exactMatch?.productPriceId) return exactMatch.productPriceId;
        }
        // 없으면 기존 클래스의 orderId 기반으로 찾기
        return resolvePurchasedProductPriceId(purchasedList, selectedProductMeta.productId, classDetail?.orderId ?? undefined);
    }, [isOpenClassEdit, selectedProductMeta.productId, selectedSalesUnitId, purchasedList, classDetail?.orderId]);

    const productDetailQueryEnabled =
        selectedProductMeta.productId > 0 &&
        (isOpenClassEdit ||
            (Number.isFinite(orgNumericIdForPurchased) &&
                orgNumericIdForPurchased > 0 &&
                (!usePurchasedProductList || !purchasedProductsFetching)));

    const { data: productDetail, isLoading: productDetailLoading } = useClassFormProductDetail(selectedProductMeta.productId, {
        titleFallback: selectedProductMeta.title,
        typeFallback: selectedProductMeta.type,
        purchasedPriceRow: isOpenClassEdit ? openClassPurchasedRow ?? undefined : orgPurchasedRowForPayload,
        enabled: productDetailQueryEnabled,
        staleTime: 0,
        refetchOnMount: 'always',
    });
    const priceOptions = useMemo(
        () => buildClassPriceSelectOptions(productDetail?.prices, selectedSalesUnitId, t('edit.salesUnitLegacyOption')),
        [productDetail?.prices, selectedSalesUnitId, t]
    );
    const selectedPriceOption = priceOptions.find((p) => p.productPriceId === selectedSalesUnitId);
    
    // 선택된 프로덕트의 주문 만료일 (수강 종료일 제한용) - 기관 클래스만 적용
    const productExpiresAt = useMemo(() => {
        if (isOpenClassEdit) return null; // 오픈 클래스는 expiresAt 제한 없음
        if (!orgPurchasedRowForPayload?.expiresAt) return null;
        const date = new Date(orgPurchasedRowForPayload.expiresAt);
        return date.toISOString().split('T')[0]; // YYYY-MM-DD 형식
    }, [isOpenClassEdit, orgPurchasedRowForPayload]);
    
    // 대면 클래스: 모집기간 최대 종료일 계산 (수강기간 종료일 - 프로덕트 유효기간)
    const maxRecruitmentEndDate = useMemo(() => {
        if (participationMethod !== 'inPerson' || !endDate) return endDate || undefined;
        
        const selectedPrice = priceOptions.find((p) => p.productPriceId === selectedSalesUnitId);
        if (!selectedPrice) return endDate || undefined;
        
        const calculatedMax = calculateMaxRecruitmentEndDate(
            endDate,
            selectedPrice.validityPeriod,
            selectedPrice.validityPeriodUnit
        );
        
        return calculatedMax || endDate || undefined;
    }, [participationMethod, endDate, priceOptions, selectedSalesUnitId]);

    // Fetch organizations for admin
    const { data: organizationsData } = useOrganizations(
        { page: 1, pageSize: 1000 },
        { enabled: isAdmin }
    );
    const organizationList = useMemo(
        () => organizationsData?.organizationList ?? [],
        [organizationsData?.organizationList]
    );
    const { data: partnerOrganizationsData, isLoading: isPartnerLoading } = useOrganizationTitleList(
        { isPartner: true },
        { enabled: isAdmin && isOpenClassEdit }
    );
    const partnerOrganizationList = partnerOrganizationsData?.organizations ?? [];

    const [scenarioModalOpen, setScenarioModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMsg, setAlertMsg] = useState('');
    const [navigateOnClose, setNavigateOnClose] = useState(false);
    /** 프로덕트 셀렉트를 사용자가 바꾼 뒤에만 상세 커리큘럼을 GET /product 기반으로 덮어씀(초기 로드는 GET /class/detail만) */
    const userChangedProductRef = useRef(false);
    /** 판매단위(가격) 셀렉트 — 구매 목록으로 복원한 id를 사용자가 바꾸지 않은 경우에만 자동 동기화 */
    const userChangedSalesUnitRef = useRef(false);

    // Validation state
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    /** 기관클래스: 구매 API productPrice 우선, 로딩 전에는 상세의 productPriceId 유지. 오픈클래스: 선택값(구매 목록과 동기화됨). */
    const effectiveProductPriceId = isOpenClassEdit
        ? selectedSalesUnitId
        : orgClassProductPriceId > 0
          ? orgClassProductPriceId
          : selectedSalesUnitId;

    /** GET /class/order/.../user-count — 기관클래스는 선택된 프로덕트의 orderId, 오픈클래스는 원래 orderId */
    const resolvedOrderIdForUserCount = useMemo(() => {
        if (isOpenClassEdit) {
            // 오픈클래스: 원래 클래스의 orderId 그대로 사용
            return classDetail?.orderId ?? undefined;
        }
        // 기관클래스: 선택된 프로덕트의 구매 행에서 orderId 가져오기
        const fromRow = orgPurchasedRowForPayload?.orderId;
        if (fromRow != null && Number(fromRow) > 0) {
            return Math.floor(Number(fromRow));
        }
        return undefined;
    }, [isOpenClassEdit, classDetail?.orderId, orgPurchasedRowForPayload?.orderId]);

    /** GET /class/order/.../user-count — 오픈클래스는 주문+구매행 기준 productPriceId(정원 30 등) 우선 */
    const orderCountProductPriceId = useMemo(() => {
        if (!resolvedOrderIdForUserCount) return 0;
        if (isOpenClassEdit) {
            return openClassResolvedProductPriceId > 0 ? openClassResolvedProductPriceId : selectedSalesUnitId;
        }
        return orgClassProductPriceId > 0 ? orgClassProductPriceId : selectedSalesUnitId;
    }, [
        resolvedOrderIdForUserCount,
        isOpenClassEdit,
        openClassResolvedProductPriceId,
        selectedSalesUnitId,
        orgClassProductPriceId,
    ]);

    const showParticipantLimit = !isOpenClassEdit || participationMethod === 'inPerson';
    const { data: orderUserCountData } = useOrderUserCount(
        resolvedOrderIdForUserCount,
        orderCountProductPriceId > 0 ? orderCountProductPriceId : undefined,
        { enabled: !!resolvedOrderIdForUserCount && orderCountProductPriceId > 0 && showParticipantLimit }
    );
    const resolvedAvailableCapacity = resolveOrderAvailableCapacity(orderUserCountData);
    const savedMaxParticipants = classDetail?.maxParticipants ?? 0;
    /**
     * user-count의 usedCapacity에는 수정 중인 클래스의 정원이 포함되지만, PUT 검증은 이 클래스를 제외하고 계산한다.
     * 따라서 클래스가 이미 묶여 있는 주문·판매단위(OrderItem) 그대로 수정할 때만 기존 정원을 되돌려준다.
     * 복제(신규 생성)이거나 다른 주문/판매단위로 바꾼 경우 이 클래스는 해당 OrderItem 사용량에 없으므로 더하지 않는다.
     */
    const reusableCapacity = useMemo(() => {
        if (isDuplicateMode) return 0;
        const isSameOrderItem =
            resolvedOrderIdForUserCount != null &&
            resolvedOrderIdForUserCount === classDetail?.orderId &&
            orderCountProductPriceId > 0 &&
            orderCountProductPriceId === classDetail?.productPriceId;
        return isSameOrderItem ? savedMaxParticipants : 0;
    }, [isDuplicateMode, resolvedOrderIdForUserCount, classDetail?.orderId, classDetail?.productPriceId, orderCountProductPriceId, savedMaxParticipants]);
    /** 수정 화면: 주문 잔여 + 이 클래스가 같은 OrderItem에서 이미 쓰고 있는 정원 = 설정 가능한 최대 인원 */
    const maxAllowedParticipants = useMemo(() => {
        if (resolvedAvailableCapacity == null) return null;
        return resolvedAvailableCapacity + reusableCapacity;
    }, [resolvedAvailableCapacity, reusableCapacity]);

    const isMaxParticipantsExceeded = useMemo(() => {
        if (!showParticipantLimit || maxAllowedParticipants == null) return false;
        return maxParticipants > maxAllowedParticipants;
    }, [showParticipantLimit, maxAllowedParticipants, maxParticipants]);

    /** PER_DEVICE 등 인원 제한이 없는 프로덕트 여부 (availableCapacity가 null) */
    const hasUnlimitedOrderCapacity = useMemo(
        () => showParticipantLimit && orderUserCountData !== undefined && resolvedAvailableCapacity == null,
        [showParticipantLimit, orderUserCountData, resolvedAvailableCapacity]
    );

    const handleMaxParticipantsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const parsed = parseInt(event.target.value, 10);
        setMaxParticipants(Number.isFinite(parsed) ? parsed : 0);
    };

    /** 주문 잔여에 맞춰 정원 입력값 보정 */
    useEffect(() => {
        if (!showParticipantLimit || !orderUserCountData || !classDetail) return;
        const cap = resolveOrderAvailableCapacity(orderUserCountData);
        // PER_DEVICE 등 무제한: maxParticipants를 0으로 설정
        if (cap == null) {
            setMaxParticipants(0);
            return;
        }
        // PER_USER: 기존 클래스 정원은 유지, 허용 범위만 초과 시 축소
        if (!Number.isFinite(cap) || cap < 0) return;
        const maxAllowed = cap + reusableCapacity;
        setMaxParticipants((prev) => (prev > maxAllowed ? maxAllowed : prev));
    }, [orderUserCountData, showParticipantLimit, classDetail, reusableCapacity]);

    /** 구매 목록으로 복원한 판매단위 id가 상세에 저장된 값과 다르면, 사용자가 판매단위를 직접 바꾸기 전에 셀렉트·저장값을 맞춤 */
    useEffect(() => {
        if (!isOpenClassEdit || !classDetail?.orderId || userChangedSalesUnitRef.current) return;
        if (openClassResolvedProductPriceId <= 0) return;
        if (openClassResolvedProductPriceId !== selectedSalesUnitId) {
            setSelectedSalesUnitId(openClassResolvedProductPriceId);
        }
    }, [isOpenClassEdit, classDetail?.orderId, openClassResolvedProductPriceId, selectedSalesUnitId]);

    const createMutation = useCreateClass();
    const updateMutation = useUpdateClass(classId);

    const selectedScenarioIds = useMemo(() => curriculumItems
        .filter((i): i is CurriculumItem & { type: 'scenario' } => i?.type === 'scenario')
        .map((i) => {
            const d = i.data as ScenarioDetail & { scenarioId?: number };
            return d.scenarioId ?? d.id;
        })
        .filter((id): id is number => id != null), [curriculumItems]);

    const handleAddScenariosFromModal = (scenarios: ScenarioDetail[]) => {
        if (!scenarios?.length) return;
        const existingIds = new Set(selectedScenarioIds);
        const toAdd = scenarios.filter((d) => !existingIds.has(d.id));
        if (!toAdd.length) return;
        const newItems: CurriculumItem[] = toAdd.map((detail) => ({
            id: `scenario-${detail.id}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            type: 'scenario' as const,
            data: detail,
        }));
        setCurriculumItems((prev) => [...prev, ...newItems]);
        setScenarioModalOpen(false);
    };

    // 실제 API 데이터 → 폼 초기화
    useEffect(() => {
        if (!classDetail) return;

        if (isDuplicateMode) {
            setClassType(classDetail.isOpenClass ? 'openClass' : 'orgClass');
        }

        // 초기 로드 시에는 classDetail의 정보만 사용 (사용자가 프로덕트를 변경하기 전까지는 덮어쓰지 않음)
        userChangedProductRef.current = false;
        userChangedSalesUnitRef.current = false;
        {
            const triple = mergeTitleDescriptionTripleFromClassLike(
                classDetail.title,
                classDetail.description,
                classDetail.translations as ClassTranslationInput[] | undefined
            );
            if (isDuplicateMode) {
                const baseTitle = (classDetail.title ?? '').trim();
                const suffix = t('edit.duplicateNameSuffix');
                triple.ko = { ...triple.ko, title: baseTitle ? `${baseTitle} - ${suffix}` : suffix };
            }
            setClassI18n(triple);
        }
        setThumbnail(classDetail.thumbnailImageUrl ?? '');
        setIsActive(classDetail.isActive !== false);
        setMaxParticipants(classDetail.maxParticipants ?? 30);
        setParticipationMethod(classDetail.isOnlineClass !== false ? 'online' : 'inPerson');

        if (classDetail.classStartDate) setStartDate(classDetail.classStartDate.split('T')[0]);
        if (classDetail.classEndDate) setEndDate(classDetail.classEndDate.split('T')[0]);
        if (classDetail.recruitmentStartDate) setRecruitmentStartDate(classDetail.recruitmentStartDate.split('T')[0]);
        if (classDetail.recruitmentEndDate) setRecruitmentEndDate(classDetail.recruitmentEndDate.split('T')[0]);

        if (classDetail.organizationId) {
            setSelectedOrganizationId(String(classDetail.organizationId));
        }
        // 오픈클래스인 경우 partnerOrganizationId 로드
        if (isOpenClassEdit && classDetail.partnerOrganizationId) {
            setPartnerOrgId(String(classDetail.partnerOrganizationId));
        }

        // 오픈클래스, 기관클래스 모두 productPriceId를 선택값으로 사용
        if (classDetail.productPriceId != null && Number(classDetail.productPriceId) > 0) {
            setSelectedProductPriceId(Number(classDetail.productPriceId));
        } else {
            setSelectedProductPriceId(0);
        }
        
        // 판매단위 (오픈클래스 전용)
        if (classDetail.productPriceId != null && Number(classDetail.productPriceId) > 0) {
            setSelectedSalesUnitId(Number(classDetail.productPriceId));
        } else {
            setSelectedSalesUnitId(0);
        }

        const detailItems = classDetail.classItemList ?? classDetail.itemList ?? [];
        const totalScenarios = detailItems.length;
        const rawMin = classDetail.minScenarioCompletionCount;
        const hasApiMin =
            rawMin != null && typeof rawMin === 'number' && Number.isFinite(rawMin) && rawMin > 0;
        const minCount = hasApiMin ? Math.max(1, Math.floor(rawMin)) : Math.max(1, totalScenarios);
        if (minCount > 0) {
            setUseCompletionRequirements(true);
            /**
             * 전체: 백엔드가 최소 이수 개수를 주지 않을 때만(현재 커리큘럼 길이와 동적 연동).
             * 직접입력: API에 숫자가 있으면 항상 표시 — 예전 로직(minCount >= totalScenarios면 전체)은
             * 3/3처럼 같아도 전체로만 보여 직접입력 3과 구분되지 않음. totalScenarios===0일 때 3>=0으로 전체로 깨지던 것도 방지.
             */
            setCompletionRequirements({
                minScenarioMode: hasApiMin ? 'custom' : 'all',
                minScenarios: minCount,
                minPassingScore: classDetail.minPassingScore ?? 70,
            });
        }

        const items: CurriculumItem[] = detailItems.map((item, index) => {
            const itemRec = item as ClassItemInfoDto & Record<string, unknown>;
            if (item.type === 'LECTURE') {
                const lectureSec = pickClassItemExpectedPlaySeconds(itemRec);
                return {
                    id: `edit-lecture-${index}-${item.lectureId ?? index}`,
                    type: 'video' as const,
                    data: {
                        id: String(item.lectureId ?? index),
                        title: item.lectureTitle ?? item.title ?? '',
                        url: item.lectureVideoUrl ?? '',
                        duration: durationLabelFromPlaySeconds(lectureSec),
                        author: { name: item.lectureCreatorName ?? '', type: 'individual' as const },
                        description: item.lectureDescription ?? '',
                    } as unknown as VideoLecture,
                };
            }

            const playSeconds = pickClassItemExpectedPlaySeconds(itemRec) ?? 0;
            return {
                id: `edit-scenario-${index}-${item.scenarioId ?? index}`,
                type: 'scenario' as const,
                data: {
                    scenarioId: item.scenarioId ?? 0,
                    title: item.scenarioTitle ?? item.title ?? `시나리오 #${item.scenarioId}`,
                    scenarioCategoryList: [],
                    version: '',
                    difficulty: 0,
                    createdAt: '',
                    thumbnailImageUrl: '',
                    expectedPlayTimeSeconds: playSeconds,
                    duration: durationLabelFromPlaySeconds(playSeconds > 0 ? playSeconds : undefined),
                    status: 'active' as const,
                    deviceNames: item.deviceType ? [item.deviceType] : [],
                    scenarioDeviceTypes: (itemRec as Record<string, unknown>).scenarioDeviceTypes as string[] | undefined ?? [],
                } as unknown as ScenarioDetail,
            };
        });
        setCurriculumItems(items);
    }, [classDetail, isDuplicateMode, t]);

    // 파트너 목록에 없는 선택값 제거(오픈클래스는 제휴 기관만)
    // - 마스터: 파트너 목록 훅이 꺼져 있어 목록이 항상 비어 있음 → 지우면 안 됨(classDetail.organizationId 유지)
    // - 어드민: 상세로 채운 직후 목록이 아직 로딩 중이면 빈 배열이라 오탐으로 지워짐 → 로딩 끝·목록 확정 후에만 검증
    useEffect(() => {
        if (!isOpenClassEdit || !partnerOrgId) return;
        if (!isAdmin) return;
        if (isPartnerLoading) return;
        if (partnerOrganizationList.length === 0) return;
        const stillValid = partnerOrganizationList.some((o) => String(o.id) === partnerOrgId);
        if (!stillValid) setPartnerOrgId('');
    }, [isOpenClassEdit, isAdmin, isPartnerLoading, partnerOrganizationList, partnerOrgId]);

    // 사용자가 프로덕트를 바꾼 경우에만 생성 페이지처럼 내용/커리큘럼을 새 프로덕트 기준으로 갱신(초기값은 GET /class/detail의 classItemList만 사용)
    useEffect(() => {
        if (!productDetail || !selectedProductMeta.productId || !userChangedProductRef.current) return;

        setClassI18n({
            ...createEmptyTitleDescTriple(),
            ko: { title: productDetail.title ?? '', description: productDetail.description ?? '', offlineClassDescription: '' },
        });
        const items: CurriculumItem[] = (productDetail.items ?? []).map((item, idx) => {
            const itemId = `load-${item.scenarioId ?? item.lectureId ?? idx}-${idx}`;
            // productDetail.items는 expectedPlayTimeSeconds 필드를 사용 (scenarioExpectedPlayTimeSeconds가 아님)
            const playSec = item.expectedPlayTimeSeconds ?? 0;
            if (item.itemType === 'LECTURE') {
                return {
                    id: itemId,
                    type: 'video' as const,
                    data: {
                        id: String(item.lectureId ?? idx),
                        title: item.lectureTitle ?? item.title ?? '',
                        url: item.lectureVideoUrl ?? '',
                        duration: durationLabelFromPlaySeconds(playSec > 0 ? playSec : undefined),
                        author: { name: item.lectureCreatorName ?? '', type: 'individual' as const },
                        description: item.lectureDescription ?? '',
                    } as unknown as VideoLecture,
                };
            }
        return {
            id: itemId,
            type: 'scenario' as const,
            data: {
                scenarioId: item.scenarioId ?? 0,
                title: item.title ?? '',
                expectedPlayTimeSeconds: playSec,
                duration: durationLabelFromPlaySeconds(playSec > 0 ? playSec : undefined),
                deviceType: item.deviceType ?? 'VR',
                scenarioDeviceTypes: item.scenarioDeviceTypes ?? [],
            } as unknown as ScenarioAdminDto,
        };
        });
        setCurriculumItems(items);
    }, [productDetail, selectedProductMeta.productId]);

    // 초기 로드 시: productDetail에서 scenarioDeviceTypes만 보충 (폼 전체를 덮어쓰지 않음)
    useEffect(() => {
        if (!productDetail || !selectedProductMeta.productId || userChangedProductRef.current) return;
        if (curriculumItems.length === 0) return;

        // productDetail의 items를 scenarioId 기준으로 맵핑
        const productItemsMap = new Map<number, { scenarioDeviceTypes?: string[] }>();
        (productDetail.items ?? []).forEach((item) => {
            if (item.scenarioId && item.scenarioDeviceTypes) {
                productItemsMap.set(item.scenarioId, { scenarioDeviceTypes: item.scenarioDeviceTypes });
            }
        });

        // 현재 커리큘럼의 scenarioDeviceTypes만 업데이트
        setCurriculumItems((prev) =>
            prev.map((currItem) => {
                if (currItem.type !== 'scenario') return currItem;
                
                const scenarioData = currItem.data as ScenarioDetail & { scenarioId?: number };
                const scenarioId = scenarioData.scenarioId ?? scenarioData.id;
                if (!scenarioId) return currItem;

                const productItem = productItemsMap.get(scenarioId);
                if (!productItem?.scenarioDeviceTypes) return currItem;

                // scenarioDeviceTypes만 업데이트
                return {
                    ...currItem,
                    data: {
                        ...scenarioData,
                        scenarioDeviceTypes: productItem.scenarioDeviceTypes,
                    } as unknown as ScenarioDetail,
                };
            })
        );
    }, [productDetail, selectedProductMeta.productId, curriculumItems.length]);

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!(['ko', 'en', 'ar', 'id', 'mn'] as const).some((c) => classI18n[c].title.trim())) {
            newErrors.className = t('edit.errorClassName');
        }

        const showParticipationPeriod = !isOpenClassEdit || participationMethod === 'inPerson';
        if (showParticipationPeriod && !startDate) {
            newErrors.startDate = t('edit.errorStartDate');
        }
        if (showParticipationPeriod && !endDate) {
            newErrors.endDate = t('edit.errorEndDate');
        }
        if (showParticipationPeriod && startDate && endDate && new Date(startDate) > new Date(endDate)) {
            newErrors.endDate = t('edit.errorEndDateAfter');
        }
        if (isOpenClassEdit && participationMethod === 'inPerson') {
            if (recruitmentStartDate && recruitmentEndDate && new Date(recruitmentStartDate) > new Date(recruitmentEndDate)) {
                newErrors.recruitmentEndDate = t('create.errorRecruitmentEndDateAfter');
            }
        }

        // 오픈클래스 온라인은 인원 제한 없음 — 검증 제외
        const needsParticipantLimit = !isOpenClassEdit || participationMethod === 'inPerson';
        // PER_DEVICE(무제한) 제외하고 인원 검증
        if (needsParticipantLimit && !hasUnlimitedOrderCapacity) {
            if (maxAllowedParticipants === 0) {
                newErrors.maxParticipants = '등록 가능한 사용자 수가 없습니다.';
            } else if (!maxParticipants || maxParticipants < 1) {
                newErrors.maxParticipants = t('edit.errorMaxParticipants');
            } else if (maxAllowedParticipants != null && maxParticipants > maxAllowedParticipants) {
                newErrors.maxParticipants = t('edit.errorParticipantsExceeded');
            } else if (!isAdmin && licenseInfo.type === 'user' && maxParticipants > licenseInfo.count) {
                newErrors.maxParticipants = t('edit.errorMaxParticipantsLicense', { count: licenseInfo.count });
            }
        }

        if (isAdmin && !isOpenClassEdit && !selectedOrganizationId) {
            newErrors.organization = t('edit.errorOrganization');
        }
        if (selectedProductMeta.productId <= 0) {
            newErrors.productId = t('edit.errorProductRequired');
        }
        if (effectiveProductPriceId <= 0) {
            newErrors.productPriceId = isOpenClassEdit
                ? '판매단위를 선택해주세요.'
                : '구매 프로덕트에 연결된 가격(productPrice) 정보가 없습니다. 목록을 새로고침하거나 담당자에게 문의해 주세요.';
        }

        if (curriculumItems.length === 0) {
            newErrors.scenarios = t('edit.errorCurriculum');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const showAlert = (msg: string, goBack = false) => {
        setAlertMsg(msg);
        setNavigateOnClose(goBack);
        setAlertOpen(true);
    };

    const handleSubmit = async () => {
        if (isInactiveOrderProduct) return;
        if (!validateForm()) return;

        // classItemList 구성 (시나리오 + 강의 모두 포함)
        const classItemList = curriculumItems.map((item, idx) => {
            if (item.type === 'scenario') {
                const d = item.data as ScenarioDetail & { scenarioId?: number };
                const sid = d.scenarioId ?? d.id ?? 0;
                return { type: 'SCENARIO' as const, order: idx, scenarioId: sid };
            } else {
                const v = item.data as unknown as { id?: string; title?: string; url?: string; author?: { name?: string }; description?: string };
                return {
                    type: 'LECTURE' as const,
                    order: idx,
                    lectureTranslations: [
                        {
                            language: 'ko',
                            title: v.title ?? '',
                            description: v.description ?? '',
                        }
                    ],
                    lectureVideoUrl: v.url ?? '',
                    lectureCreatorName: v.author?.name ?? '',
                };
            }
        });

        const scenarioCount = classItemList.filter((i) => i.type === 'SCENARIO').length;
        const minCount = useCompletionRequirements
            ? (completionRequirements.minScenarioMode === 'all' ? scenarioCount : completionRequirements.minScenarios)
            : scenarioCount;

        let thumbnailImageUrl: string | undefined;
        if (uploadedFile) {
            try {
                thumbnailImageUrl = await uploadFileViaPresignedUrl(uploadedFile, 'EDU');
            } catch (e) {
                showAlert(e instanceof Error ? e.message : '썸네일 업로드에 실패했습니다.');
                return;
            }
        } else if (thumbnail.trim()) {
            thumbnailImageUrl = thumbnail.trim();
        }

        // organizationId는 기관클래스일 때만 전송
        // - 마스터: currentAccount 기관
        // - 어드민 기관클래스: 선택된 기관 ID
        // - 오픈클래스: organizationId 필드 자체를 전송하지 않음
        let orgId: number | undefined;
        if (!isOpenClassEdit) {
            orgId = isMaster
                ? Number(user?.currentAccount?.organizationId) || Number(classDetail?.organizationId) || 0
                : Number(selectedOrganizationId) || 0;
        }

        // 파트너 기관 ID (오픈클래스일 때만 전송)
        const partnerOrganizationId = isOpenClassEdit && partnerOrgId
            ? Number(partnerOrgId) || null
            : null;

        let classI18nForApi: TitleDescTriple;
        try {
            classI18nForApi = await withTranslationLoading(
                () => translateTitleDescTripleIfNeeded(classI18n, formLocale),
                { loadingMessage: '클래스 정보 번역 중...' }
            );
        } catch (e) {
            showAlert(e instanceof Error ? `자동 번역에 실패했습니다. ${e.message}` : '자동 번역에 실패했습니다.');
            return;
        }
        // - 오픈클래스: 구매 행의 orderId
        // - 기관클래스: 선택된 프로덕트의 구매 행에서 orderId 가져오기
        // - 없으면 null
        let resolvedOrderId: number | undefined;
        if (isOpenClassEdit) {
            // 오픈클래스: 구매 행에 orderId가 있으면 사용
            const fromRow = openClassPurchasedRow?.orderId;
            if (fromRow != null && Number(fromRow) > 0) {
                resolvedOrderId = Math.floor(Number(fromRow));
            }
        } else {
            // 기관클래스: 선택된 프로덕트의 구매 행에서 orderId 가져오기
            const fromRow = orgPurchasedRowForPayload?.orderId;
            if (fromRow != null && Number(fromRow) > 0) {
                resolvedOrderId = Math.floor(Number(fromRow));
            }
        }
        
        // maxParticipants 계산: PER_DEVICE(무제한) → 0, 오픈클래스 온라인(PER_USER) → 1, 나머지 → 입력값
        const maxParticipantsForApi = hasUnlimitedOrderCapacity
            ? 0
            : isOpenClassEdit && participationMethod === 'online'
              ? 1
              : maxParticipants;

        const payload = {
            // organizationId는 기관클래스일 때만 포함
            ...(orgId != null ? { organizationId: orgId } : {}),
            productId: selectedProductMeta.productId,
            translations: buildClassTranslationTriple(coalesceEmptyTitleDescSlots(classI18nForApi)),
            thumbnailImageUrl: thumbnailImageUrl || undefined,
            isActive,
            isOpenClass: isOpenClassEdit,
            isOnlineClass: participationMethod === 'online',
            productPriceId: effectiveProductPriceId,
            classItemList,
            minScenarioCompletionCount: Math.max(minCount, 1),
            minPassingScore: useCompletionRequirements ? completionRequirements.minPassingScore : 70,
            maxParticipants: maxParticipantsForApi,
            classStartDate: (!isOpenClassEdit || participationMethod === 'inPerson') && startDate ? `${startDate}T00:00:00Z` : undefined,
            classEndDate: (!isOpenClassEdit || participationMethod === 'inPerson') && endDate ? `${endDate}T23:59:59Z` : undefined,
            recruitmentStartDate: isOpenClassEdit && participationMethod === 'inPerson' && recruitmentStartDate ? `${recruitmentStartDate}T00:00:00Z` : undefined,
            recruitmentEndDate: isOpenClassEdit && participationMethod === 'inPerson' && recruitmentEndDate ? `${recruitmentEndDate}T23:59:59Z` : undefined,
            // partnerOrganizationId는 오픈클래스일 때만 포함
            ...(partnerOrganizationId != null ? { partnerOrganizationId } : {}),
            ...(resolvedOrderId != null && resolvedOrderId > 0 ? { orderId: resolvedOrderId } : {}),
        };

        setIsSubmitting(true);
        try {
            if (isDuplicateMode) {
                await createMutation.mutateAsync(payload);
                showAlert(t('create.created'), true);
            } else {
                await updateMutation.mutateAsync(payload);
                showAlert(t('edit.saved'), true);
            }
        } catch (e) {
            let msg: string;
            if (e instanceof ApiError && e.status === 403) {
                msg = '기관에 속한 유저만 생성가능합니다.';
            } else if (e instanceof Error) {
                msg = e.message;
            } else {
                msg = isDuplicateMode ? t('edit.duplicateFailed') : '클래스 수정에 실패했습니다.';
            }
            showAlert(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        if (navigateOnClose) navigate(classManagementPath);
    };

    const handleCancel = () => {
        if (window.confirm(t('edit.confirmCancel'))) {
            navigate(classManagementPath);
        }
    };

    if (detailLoading) {
        return (
            <div className="flex items-center justify-center h-64 gap-3 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>클래스 정보를 불러오는 중입니다...</span>
            </div>
        );
    }

    if (detailError || (!classDetail && !detailLoading)) {
        return (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                <AlertCircle size={16} className="shrink-0" />
                <span>{detailError instanceof Error ? detailError.message : '클래스 정보를 찾을 수 없습니다.'} (classId: {classId})</span>
            </div>
        );
    }

    return (
        <>
            <PageHeader
                title={isDuplicateMode ? t('edit.pageTitleDuplicate') : t('edit.pageTitle')}
                breadcrumbs={[
                    { label: t('edit.breadcrumbManagement'), link: classManagementPath },
                    { label: isDuplicateMode ? t('edit.breadcrumbDuplicate') : t('edit.breadcrumbEdit') }
                ]}
            />

            <div className="space-y-8 pb-12 max-w-4xl">
                <section className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="space-y-4">

                        {/* ── 공통 필드 ── */}

                        {/* 파트너 선택 (오픈클래스만) */}
                        {isOpenClassEdit && (
                            <>
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                        {t('edit.selectPartner')}
                                    </label>
                                    <div className="flex-1">
                                        <Select
                                            value={partnerOrgId || 'none'}
                                            onValueChange={(v) => setPartnerOrgId(v === 'none' ? '' : v)}
                                            disabled={isPartnerLoading}
                                        >
                                            <SelectTrigger className="w-80">
                                                <SelectValue
                                                    placeholder={
                                                        isPartnerLoading
                                                            ? '파트너 기관 조회 중...'
                                                            : t('edit.selectPartnerPlaceholder')
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">{t('edit.partnerOrgNone')}</SelectItem>
                                                {partnerOrganizationList.map((org) => (
                                                    <SelectItem key={org.id} value={String(org.id)}>
                                                        {org.title}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="border-t border-gray-200" />
                            </>
                        )}

                        {/* 고객사 선택 (기관클래스 + 어드민만) */}
                        {!isOpenClassEdit && isAdmin && (
                            <>
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                        {t('edit.selectCustomer')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex-1">
                                        <Select
                                            value={selectedOrganizationId}
                                            onValueChange={(v) => {
                                                setSelectedOrganizationId(v);
                                                setSelectedProductPriceId(0);
                                                setSelectedSalesUnitId(0);
                                            }}
                                        >
                                            <SelectTrigger className="w-80">
                                                <SelectValue placeholder={t('edit.selectCustomerPlaceholder')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {organizationList.map((org) => (
                                                    <SelectItem key={org.organizationId} value={org.organizationId.toString()}>
                                                        {org.title}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.organization && (
                                            <p className="text-sm text-red-600 mt-1">{errors.organization}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="border-t border-gray-200" />
                            </>
                        )}

                        {/* 프로덕트 선택 */}
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                {t('edit.productSelect')}
                            </label>
                            <div className="flex-1">
                                <Select
                                    value={selectedProductPriceId > 0 ? String(selectedProductPriceId) : ''}
                                    onValueChange={(v) => {
                                        userChangedProductRef.current = true;
                                        setSelectedProductPriceId(Number(v));
                                        setSelectedSalesUnitId(0);
                                    }}
                                    disabled={!isOpenClassEdit && !selectedOrganizationId}
                                >
                                    <SelectTrigger className="w-80">
                                        <SelectValue
                                            placeholder={
                                                !isOpenClassEdit && !selectedOrganizationId
                                                    ? t('edit.productPlaceholderOrgFirst')
                                                    : isOpenClassEdit
                                                      ? t('edit.productPlaceholderOpen')
                                                      : usePurchasedProductList && purchasedProductsFetching
                                                        ? t('create.productPlaceholderPurchasedLoading')
                                                        : productOptions.length === 0
                                                          ? t('edit.productPlaceholderNoPurchase')
                                                          : t('edit.productPlaceholderOrg')
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {productOptions.map((p, idx) => {
                                            // 오픈클래스, 기관클래스 모두 productPriceId 사용
                                            const selectValue = p.productPriceId ?? p.productId;
                                            return (
                                                <SelectItem key={`product-${selectValue}-${idx}`} value={String(selectValue)}>
                                                    {p.title}
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                                {errors.productId && (
                                    <p className="text-sm text-red-600 mt-1">{errors.productId}</p>
                                )}
                                {!isOpenClassEdit && errors.productPriceId && (
                                    <p className="text-sm text-red-600 mt-1">{errors.productPriceId}</p>
                                )}
                                {isInactiveOrderProduct && (
                                    <p className="text-sm text-red-600 mt-1">{t('edit.inactiveOrderProductNotice')}</p>
                                )}
                            </div>
                        </div>

                        {/* 클래스명 / 소개 (ko, en, ar) */}
                        <div className="border-t border-gray-200" />
                        <div className="flex items-start gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                {t('edit.className')} / {t('edit.description')} <span className="text-red-500">*</span>
                            </label>
                            <div className="flex-1 min-w-0 w-full max-w-2xl space-y-2">
                                <Input
                                    type="text"
                                    value={classI18n[formLocale].title}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setClassI18n((prev) => ({
                                            ...prev,
                                            [formLocale]: { ...prev[formLocale], title: v },
                                        }));
                                    }}
                                    placeholder={t('edit.classNamePlaceholder')}
                                />
                                <Input
                                    multiline
                                    value={classI18n[formLocale].description}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                        const v = e.target.value;
                                        setClassI18n((prev) => ({
                                            ...prev,
                                            [formLocale]: { ...prev[formLocale], description: v },
                                        }));
                                    }}
                                    placeholder={t('edit.descriptionPlaceholder')}
                                    rows={4}
                                />
                                {errors.className && <p className="text-sm text-red-600 mt-1">{errors.className}</p>}
                            </div>
                        </div>

                        {/* 썸네일 첨부 */}
                        <div className="flex items-start gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">{t('edit.thumbnail')}</label>
                            <div className="flex-1 space-y-3">
                                <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 w-fit">
                                    <Upload size={16} className="text-gray-600" />
                                    <span className="text-sm text-gray-700">{t('edit.selectImageFile')}</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setUploadedFile(file);
                                                const reader = new FileReader();
                                                reader.onload = (event) => {
                                                    setThumbnail(event.target?.result as string);
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                        className="hidden"
                                    />
                                </label>
                                {uploadedFile && (
                                    <p className="text-sm text-gray-600">{t('edit.selectedFile')}: {uploadedFile.name}</p>
                                )}
                                {!uploadedFile && thumbnail && !thumbnail.startsWith('data:') && (
                                    <p className="text-sm text-gray-500 truncate max-w-sm" title={thumbnail}>{t('edit.currentThumbnail')}: {thumbnail}</p>
                                )}

                                {/* 썸네일 미리보기 */}
                                {thumbnail && (
                                    <div className="mt-3">
                                        <p className="text-sm text-gray-600 mb-2">{t('edit.preview')}:</p>
                                        <div className="w-32 h-32 rounded-lg border border-gray-200 overflow-hidden bg-gray-100">
                                            <img
                                                src={thumbnail}
                                                alt="Thumbnail preview"
                                                className="w-full h-full object-cover"
                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 커리큘럼 */}
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                {t('shared.curriculum')}
                            </label>
                            <div className="flex-1 space-y-3">
                                {curriculumItems.length === 0 ? (
                                    <Button type="button" variant="outline" size="sm" onClick={() => setScenarioModalOpen(true)}>
                                        <Plus size={14} className="mr-1" />{t('create.addCurriculum')}
                                    </Button>
                                ) : null}
                                {curriculumItems.length > 0 && (
                                    <CurriculumSetup items={curriculumItems} onItemsChange={setCurriculumItems} error={errors.scenarios} hideEditCompleteAndReset />
                                )}
                            </div>
                        </div>

                        {/* 이수조건 */}
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 shrink-0">
                                {t('edit.completionRequirements')}
                            </label>
                            <div className="flex-1 space-y-3">
                                {!useCompletionRequirements ? (
                                    <Button variant="outline" size="sm" onClick={() => setUseCompletionRequirements(true)}>
                                        <Plus size={14} className="mr-1" />{t('create.addCompletionRequirements')}
                                    </Button>
                                ) : (
                                    <div>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-600 w-28">{t('create.minScenariosLabel')}</span>
                                                <label className="flex items-center gap-1.5 cursor-pointer">
                                                    <input type="radio" name="minScenarioMode" checked={completionRequirements.minScenarioMode === 'all'} onChange={() => setCompletionRequirements({ ...completionRequirements, minScenarioMode: 'all' })} className="w-4 h-4 text-blue-600 border-gray-300" />
                                                    <span className="text-sm text-gray-700">{t('create.all')}</span>
                                                </label>
                                                <label className="flex items-center gap-1.5 cursor-pointer">
                                                    <input type="radio" name="minScenarioMode" checked={completionRequirements.minScenarioMode === 'custom'} onChange={() => setCompletionRequirements({ ...completionRequirements, minScenarioMode: 'custom' })} className="w-4 h-4 text-blue-600 border-gray-300" />
                                                    <span className="text-sm text-gray-700">{t('create.customInput')}</span>
                                                </label>
                                                {completionRequirements.minScenarioMode === 'custom' && (
                                                    <span className="flex items-center gap-2">
                                                        <Input type="number" min={1} max={curriculumItems.length || 1} value={completionRequirements.minScenarios} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompletionRequirements({ ...completionRequirements, minScenarios: parseInt(e.target.value, 10) || 1 })} className="w-20" />
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-600 w-28">{t('create.minPassingScoreLabel')}</span>
                                                <Input type="number" min={0} max={100} value={completionRequirements.minPassingScore} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompletionRequirements({ ...completionRequirements, minPassingScore: parseInt(e.target.value, 10) || 0 })} className="w-20" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── 판매단위: 오픈클래스만 선택 — 기관클래스는 구매 API productPrice 자동 반영 ── */}
                        {selectedProductMeta.productId > 0 && isOpenClassEdit && (
                            <>
                                <div className="border-t border-gray-200" />
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                        {t('edit.salesUnit')}
                                    </label>
                                    <div className="flex-1 space-y-3">
                                        <Select
                                            value={selectedSalesUnitId > 0 ? String(selectedSalesUnitId) : ''}
                                            onValueChange={(v) => {
                                                userChangedSalesUnitRef.current = true;
                                                setSelectedSalesUnitId(Number(v));
                                            }}
                                        >
                                            <SelectTrigger className="w-80">
                                                <SelectValue placeholder={t('edit.salesUnitPlaceholder')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {priceOptions.map((p) => (
                                                    <SelectItem key={p.productPriceId} value={String(p.productPriceId)}>
                                                        {p.title ?? `판매단위 #${p.productPriceId}`}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.productPriceId && (
                                            <p className="text-sm text-red-600 mt-1">{errors.productPriceId}</p>
                                        )}
                                        {productDetailLoading && (
                                            <p className="text-sm text-gray-500">{t('edit.salesUnitListLoading')}</p>
                                        )}
                                        {!productDetailLoading &&
                                            (productDetail?.prices?.length ?? 0) === 0 &&
                                            selectedSalesUnitId <= 0 && (
                                            <p className="text-sm text-gray-500">연결된 판매단위가 없습니다.</p>
                                        )}
                                        {selectedPriceOption && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
                                                <div className="flex">
                                                    <span className="text-gray-500 w-24 shrink-0">{t('create.usagePeriod')}</span>
                                                    <span className="text-gray-900">
                                                        {selectedPriceOption.validityPeriod != null
                                                            ? `${selectedPriceOption.validityPeriod}${selectedPriceOption.validityPeriodUnit === 'YEAR' ? '년' : selectedPriceOption.validityPeriodUnit === 'MONTH' ? '개월' : '일'}`
                                                            : '-'}
                                                    </span>
                                                </div>
                                                <div className="flex">
                                                    <span className="text-gray-500 w-24 shrink-0">{t('create.listPrice')}</span>
                                                    <span className="text-gray-900">
                                                        {formatSalesUnitMoneyCell(selectedPriceOption.regularPrice, selectedPriceOption.currency)}
                                                    </span>
                                                </div>
                                                <div className="flex">
                                                    <span className="text-gray-500 w-24 shrink-0">{t('create.discountPrice')}</span>
                                                    <span className="text-gray-900 font-medium">
                                                        {formatSalesUnitMoneyCell(selectedPriceOption.discountedPrice, selectedPriceOption.currency)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── 오픈클래스 전용 필드 ── */}
                        {isOpenClassEdit && (
                            <>
                                {/* 참여방법 */}
                                <div className="border-t border-gray-200" />
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                        {t('edit.participationMethod')}
                                    </label>
                                    <div className="flex-1 flex gap-6">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="participationMethod" value="online" checked={participationMethod === 'online'} onChange={() => setParticipationMethod('online')} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                            <span className="text-sm text-gray-700">{t('edit.online')}</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="radio" name="participationMethod" value="inPerson" checked={participationMethod === 'inPerson'} onChange={() => setParticipationMethod('inPerson')} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                                            <span className="text-sm text-gray-700">{t('edit.inPerson')}</span>
                                        </label>
                                    </div>
                                </div>

                                {/* 수강기간 (대면만 표시) */}
                                {participationMethod === 'inPerson' && (
                                    <>
                                        <div className="border-t border-gray-200" />
                                        <div className="flex items-center gap-4">
                                            <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                                {t('edit.participationPeriod')} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex-1 flex gap-4">
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">{t('edit.startDate')}</label>
                                                    <Input type="date" value={startDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)} />
                                                    {errors.startDate && <p className="text-sm text-red-600 mt-1">{errors.startDate}</p>}
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">{t('edit.endDate')}</label>
                                                    <Input type="date" value={endDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)} min={today} max={productExpiresAt || undefined} />
                                                    {errors.endDate && <p className="text-sm text-red-600 mt-1">{errors.endDate}</p>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* 모집기간 */}
                                        <div className="border-t border-gray-200" />
                                        <div className="flex items-center gap-4">
                                            <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">{t('create.recruitmentPeriod')}</label>
                                            <div className="flex-1 flex gap-4">
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">{t('edit.startDate')}</label>
                                                    <Input 
                                                        type="date" 
                                                        value={recruitmentStartDate} 
                                                        onChange={(e) => setRecruitmentStartDate(e.target.value)} 
                                                        className="w-40" 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">{t('edit.endDate')}</label>
                                                    <Input 
                                                        type="date" 
                                                        value={recruitmentEndDate} 
                                                        onChange={(e) => setRecruitmentEndDate(e.target.value)} 
                                                        min={recruitmentStartDate || undefined}
                                                        max={maxRecruitmentEndDate}
                                                        className="w-40" 
                                                    />
                                                    {errors.recruitmentEndDate && <p className="text-sm text-red-600 mt-1">{errors.recruitmentEndDate}</p>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border-t border-gray-200" />
                                        <div className="flex items-start gap-4">
                                            <label className="text-sm font-medium text-gray-700 w-32 shrink-0 pt-2">
                                                {t('create.participationGuide')}
                                            </label>
                                            <div className="flex-1 min-w-0">
                                                <Input
                                                    multiline
                                                    value={classI18n[formLocale].offlineClassDescription}
                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                                        const v = e.target.value;
                                                        setClassI18n((prev) => ({
                                                            ...prev,
                                                            [formLocale]: { ...prev[formLocale], offlineClassDescription: v },
                                                        }));
                                                    }}
                                                    placeholder={t('create.participationGuidePlaceholder')}
                                                    rows={4}
                                                />
                                            </div>
                                        </div>

                                    </>
                                )}
                            </>
                        )}

                        {/* ── 기관클래스 전용 필드 ── */}
                        {!isOpenClassEdit && (
                            <>
                                {/* 수강기간 */}
                                <div className="border-t border-gray-200" />
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                        {t('edit.participationPeriod')} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex-1 flex gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">{t('edit.startDate')}</label>
                                            <Input type="date" value={startDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)} />
                                            {errors.startDate && <p className="text-sm text-red-600 mt-1">{errors.startDate}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">{t('edit.endDate')}</label>
                                            <Input type="date" value={endDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)} min={today} max={productExpiresAt || undefined} />
                                            {errors.endDate && <p className="text-sm text-red-600 mt-1">{errors.endDate}</p>}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── 공통: 참가인원 ── */}
                        {showParticipantLimit && (
                            <>
                                <div className="border-t border-gray-200" />
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                        {t('edit.participants')}
                                    </label>
                                    <div className="flex-1 flex items-center gap-2 flex-wrap">
                                        {hasUnlimitedOrderCapacity ? (
                                            <span className="inline-flex h-10 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-brand-700">
                                                무제한
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1">
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    value={maxParticipants}
                                                    onChange={handleMaxParticipantsChange}
                                                    className={`w-24 text-left ${isMaxParticipantsExceeded ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                                                />
                                                <span className="text-sm text-gray-600">{t('edit.peopleUnit')}</span>
                                            </span>
                                        )}
                                        {isMaxParticipantsExceeded && (
                                            <span className="text-sm text-red-600 whitespace-nowrap">
                                                {t('edit.errorParticipantsExceeded')}
                                            </span>
                                        )}
                                        {!isMaxParticipantsExceeded && orderUserCountData !== undefined && (
                                            <span className="text-sm text-gray-500">
                                                {resolvedAvailableCapacity == null
                                                    ? `(${t('create.registrableUsers')}: 무제한)`
                                                    : `(${t('create.registrableUsers')}: ${maxAllowedParticipants}${t('edit.peopleUnit')}${orderUserCountData?.totalQuantity != null && orderUserCountData.totalQuantity > 0 ? ` · ${t('create.purchasedSeatTotal', { count: orderUserCountData.totalQuantity })}` : ''})`}
                                            </span>
                                        )}
                                        {!isMaxParticipantsExceeded && errors.maxParticipants && (
                                            <p className="text-sm text-red-600 ml-2">{errors.maxParticipants}</p>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* 활성 여부 */}
                        <div className="border-t border-gray-200" />
                        <div className="flex items-start py-2 gap-4">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2 shrink-0">
                                {t('edit.activeStatus')}
                            </label>
                            <div className="flex-1">
                                <ToggleLabel
                                    checked={isActive}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsActive(e.target.checked)}
                                    onLabel={t('edit.active')}
                                    offLabel={t('edit.inactive')}
                                    size="sm"
                                />
                            </div>
                        </div>

                    </div>
                </section>

                <AddScenarioModal
                    isOpen={scenarioModalOpen}
                    onClose={() => setScenarioModalOpen(false)}
                    onSelect={handleAddScenariosFromModal}
                    selectedScenarioIds={selectedScenarioIds}
                />

                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
                        {t('edit.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={
                            isSubmitting
                            || isInactiveOrderProduct
                            || isMaxParticipantsExceeded
                            || (isDuplicateMode ? createMutation.isPending : updateMutation.isPending)
                        }
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 size={14} className="animate-spin mr-1" />
                                {isDuplicateMode ? t('edit.duplicateSaving') : '수정 중...'}
                            </>
                        ) : isDuplicateMode ? (
                            t('edit.duplicateSave')
                        ) : (
                            t('edit.save')
                        )}
                    </Button>
                </div>
            </div>

            <AlertDialog isOpen={alertOpen} onClose={handleAlertClose} message={alertMsg} />
        </>
    );
}
